import React from 'react';
import { ThemeProvider, createTheme, CssBaseline, Container, Typography, Box, Button, Stack } from '@mui/material';
import { DISCORD_LINK } from './App';

async function copyToClipboard(text: string) {
    try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch { }

    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);

    ta.focus();
    ta.select();
    ta.setSelectionRange(0, ta.value.length);

    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (!ok) throw new Error('execCommand copy failed');
    return true;
}

const gradientText = {
    background: 'white',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
} as const;

export default function Referrer() {
    const [busy, setBusy] = React.useState(false);

    let affiliateCode = React.useMemo(() => new URLSearchParams(window.location.search).get('code') || '', []);

    // Add a known prefix to the affiliate code
    const knownPrefix = 'evo_affiliate_code_';
    if (affiliateCode && !affiliateCode.startsWith(knownPrefix)) {
        affiliateCode = knownPrefix + affiliateCode;
    }

    React.useEffect(() => {
        if (!affiliateCode && typeof window !== 'undefined') {
            window.location.replace('/');
        }
    }, [affiliateCode]);

    const goToDiscord = React.useCallback(() => {
        window.location.replace(DISCORD_LINK);
    }, []);

    const handleGetAppClick = React.useCallback(async (codeFromUrl: string) => {
        if (busy) return;
        setBusy(true);
        try {
            await copyToClipboard(codeFromUrl);
        } catch {
        } finally {
            setBusy(false);
            goToDiscord();
        }
    }, [busy, goToDiscord]);

    const handleGetAppWithoutCopyingClick = React.useCallback(() => {
        goToDiscord();
    }, [goToDiscord]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ position: 'relative', minHeight: '100dvh', display: 'flex', alignItems: 'center' }}>
                <Container maxWidth="md">
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h2" sx={{ ...gradientText, fontSize: { xs: 36, md: 56 }, lineHeight: 1.05, mt: 1 }}>
                            Install Evo
                        </Typography>
                        <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2}}>
                            Join the Discord to get early access
                        </Typography>
                        <Stack
                            direction={{ xs: 'column', sm: 'column' }}
                            spacing={2}
                            justifyContent="center"
                            sx={{ mt: 4, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '24px' }}
                        >
                            <Button
                                style={{ minWidth: '100%' }}
                                variant="contained"
                                disabled={busy || !affiliateCode}
                                onClick={() => handleGetAppClick(affiliateCode)}
                            >
                                {affiliateCode ? 'Join the Discord' : 'Missing code in URL'}
                            </Button>

                            <Button
                                style={{ backgroundColor: 'transparent', color: 'text.secondary', minWidth: '100%' }}
                                variant="contained"
                                onClick={handleGetAppWithoutCopyingClick}
                            >
                                Join without Copying referral code
                            </Button>
                        </Stack>
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
}

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: 'rgba(138, 48, 247, 0.18)' },
        secondary: { main: '#00E5FF' },
        background: { default: '#0B0B0F', paper: '#111218' },
        text: { primary: '#EDEDF5', secondary: 'rgba(237,237,245,0.7)' }
    },
    typography: {
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
        h1: { fontWeight: 800, letterSpacing: -1 },
        h2: { fontWeight: 800, letterSpacing: -0.5 },
        h3: { fontWeight: 800 },
        button: { textTransform: 'none', fontWeight: 700 }
    },
    shape: { borderRadius: 16 },
    components: { MuiButton: { styleOverrides: { root: { borderRadius: 9999 } } } }
});